<template>
  <internal-process
    class="update-fund-review-status-container"
    @go-back="backHandler"
    :process-name="processTitle"
    :with-main-background="withMainBackground"
    :with-footer-background="withFooterBackground"
  >
    <template v-if="reviewLoading">
      <mds-loader />
    </template>
    <template v-else-if="errorMsg">
     <div class="fund-error">
        <p>Something went wrong! We could not load the content for this fund.</p>
        <p>Please refresh the page or try again later.</p>
      </div>
    </template>
    <template v-else>
      <div class="update-status-container">
        <div class="fund-name-container">
          <h4 class="field-value">{{fundDetails.standardName}}</h4>
        </div>
        <div class="fund-details-container">
          <div class="fund-details fund-border">
            <span class="field-name">Provider Name:</span>
            <span class="field-value">{{fundDetails.clientName}}</span>
          </div>
          <div class="fund-details fund-border">
            <span class="field-name">FundID:</span>
            <span class="field-value">{{fundDetails.fundID}}</span>
          </div>
          <div class="fund-details fund-border">
            <span class="field-name">SecID:</span>
            <span class="field-value">{{fundDetails.secId}}</span>
          </div>
          <div class="fund-details">
            <span class="field-name">Ticker:</span>
            <span class="field-value">{{fundDetails.ticker}}</span>
          </div>
        </div>
      </div>
      <mds-tabs :content="tabContent" @mds-tabs-item-active="setActiveItem">
      </mds-tabs>
      <div v-if="currentTab === 'update_review_status'">
        <div class="update-review-status-container">
          <div class="current-status-container">
            <span class="status-label">Current Status:</span>
            <span class="status-value">
              <ReviewStatusIcons
                :status="[currentReviewStatus]"
                :fullLength="true"
              />
            </span>
          </div>
          <mds-form>
            <mds-select
              class="change-status-box"
              required
              label="Change Status"
              placeholder="Make Selection"
              :error="statusError"
              :options="changeStatusList"
              v-model="newReviewStatus"
            >
            </mds-select>
            <mds-section
              class="share-class-container"
              collapsible
              expanded
              container
              title="Shared Classes"
              :size="7"
              bold
            >
              <mds-loader v-if="shareClassLoading" />
              <mds-table v-else>
                <mds-thead>
                    <mds-th style="width: 40%;"> Fund Name </mds-th>
                    <mds-th style="width: 20%;"> Ticker </mds-th>
                    <mds-th> Current Review Status </mds-th>
                    <mds-th> New Review Status </mds-th>
                </mds-thead>
                <mds-tbody>
                    <mds-tr  v-for="(row, i) in shareClassFundData" :key="i">
                        <mds-td> {{row.fundName}} </mds-td>
                        <mds-td> {{row.ticker}} </mds-td>
                        <mds-td>
                          <ReviewStatusIcons
                            :status="[row.reviewStatus]"
                            :fullLength="true"
                          />
                        </mds-td>
                        <mds-td>
                          <ReviewStatusIcons
                            v-if="newReviewStatus"
                            :status="[newReviewStatus]"
                            :fullLength="true"
                          />
                          <span v-else>{{ 'Make Selection' }}</span>
                        </mds-td>
                    </mds-tr>
                </mds-tbody>
              </mds-table>
            </mds-section>
            <div class="add-notes-container">
              <mds-textarea
                class="add-notes-box"
                label="Add Notes"
                placeholder="Input Text"
                maxlength="1000"
                v-model="notesData"
              >
              </mds-textarea>
              <div class="notes-count">{{notesData.length}} / {{notesMaxChar}}</div>
            </div>
            <mds-button variation="primary" @click.prevent="handleSave">Save Changes</mds-button>
          </mds-form>
        </div>
        <FundReviewNotesList ref="notesList" :fundId="fundId"/>
      </div>
      <div class="update-fund-status-container" v-if="currentTab === 'update_fund_status'">
        <div v-if="isFundPartOfSeries" class="fund-series-warning">
          <!-- eslint-disable-next-line max-len -->
          <p>Fund status cannot be changed for this fund through fund review panel as this fund is part of a series fund - {{fundDetails.fundSeriesId}}.</p>
          <p>Please change the fund status through 1 client view universe screen.</p>
        </div>
        <div v-else>
          <Approve v-if="showApproveComponent" :data="fundStatusData"></Approve>
          <ChangeStatus v-else :data="fundStatusData"></ChangeStatus>
        </div>
      </div>
    </template>

  </internal-process>
</template>

<script>
import MdsForm from '@mds/form';
import MdsSelect from '@mds/select';
import MdsTextarea from '@mds/textarea';
import { MdsButton } from '@mds/button';
import MdsSection from '@mds/section';
import { MdsTabs } from '@mds/tabs';
import {
  MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd,
} from '@mds/data-table';
import ReviewStatusIcons from '@/components/atoms/ReviewStatusIcons';
import FundReviewNotesList from '@/components/organisms/FundReviewNotesList';
import ChangeStatus from '@/components/organisms/ChangeStatus';
import Approve from '@/components/organisms/Approve';
import * as FundReviewService from '@/services/FundReview';
import { mapState } from 'vuex';

const CHANGE_STATUS_LIST = [
  {
    text: 'Reviewed',
    value: 'Reviewed',
  },
  {
    text: 'Review Requested',
    value: 'Review Requested',
  },
  {
    text: 'No Review Requested',
    value: 'No Review Requested',
  },
];

const REVIEW_ACTION = {
  only_review: 'Review Status Updated',
  review_and_note: 'Review Status & Note Updated',
};

const NOTE_DUMMY_TEXT = 'Review status updated without note.';

export default {
  name: 'UpdateFundReviewStatus',
  components: {
    Approve,
    MdsForm,
    MdsSelect,
    MdsButton,
    MdsTextarea,
    MdsSection,
    MdsTabs,
    MdsTable,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
    FundReviewNotesList,
    ChangeStatus,
    ReviewStatusIcons,
  },
  props: {
    fundId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      processTitle: 'Update Review Or Fund Status',
      withMainBackground: false,
      withFooterBackground: false,
      changeStatusList: CHANGE_STATUS_LIST,
      currentReviewStatus: '',
      newReviewStatus: '',
      statusError: false,
      notesData: '',
      notesMaxChar: 1000,
      reviewLoading: false,
      fundDetails: '',
      errorMsg: false,
      fundStatusData: null,
      tabContent: [{
        text: 'Update Review Status',
        id: 'update_review_status',
        active: true,
      },
      {
        text: 'Update Fund Status',
        id: 'update_fund_status',
        active: false,
      }],
      currentTab: 'update_review_status',
      shareClassFundData: [],
      shareClassLoading: false,
    };
  },
  computed: {
    ...mapState({
      selectedClient: (state) => state.grid.client.selected,
    }),
    showApproveComponent() {
      return this.fundDetails?.fundStatusName === 'Removed' || this.fundDetails?.fundStatusName === 'None';
    },
    isFundPartOfSeries() {
      return this.fundDetails.isSeriesFund;
    },
  },
  watch: {
    newReviewStatus(value) {
      if (value) {
        this.statusError = false;
      }
    },
  },
  created() {
    this.loadFundDetails();
  },
  methods: {
    backHandler() {
      if (this.selectedClient) {
        this.$router.push(
          {
            name: 'clientGrid',
            params: { clientId: this.selectedClient },
          },
        );
      } else {
        this.$router.push({ name: 'grid' });
      }
    },
    handleSave() {
      if (!this.newReviewStatus) {
        this.statusError = true;
      } else {
        this.saveReviewStatus();
      }
    },
    saveReviewStatus() {
      let reviewActionText = null;
      let noteToSave = null;
      if (this.notesData) {
        reviewActionText = REVIEW_ACTION.review_and_note;
        noteToSave = this.notesData;
      } else {
        reviewActionText = REVIEW_ACTION.only_review;
        noteToSave = NOTE_DUMMY_TEXT;
      }
      let requestPayload;

      if (this.shareClassFundData.length > 0) {
        requestPayload = this.shareClassFundData.map((fund) => {
          return {
            fiduciaryFundId: fund.id,
            reviewStatus: this.newReviewStatus,
            reviewAction: reviewActionText,
            notes: noteToSave,
          };
        });
      } else {
        requestPayload = [{
          fiduciaryFundId: this.fundId,
          reviewStatus: this.newReviewStatus,
          reviewAction: reviewActionText,
          notes: noteToSave,
        }];
      }

      FundReviewService.addFundReview(requestPayload).then((response) => {
        if (response.status.success) {
          this.$store.dispatch('openNotification', {
            message: 'Update successful.',
          });
          this.currentReviewStatus = this.newReviewStatus;
          this.$refs.notesList.resetNotesListPagination();
          this.updateShareClassData();
          this.resetFields();
        }
      }).catch((error) => {
        this.handleError(error);
      });
    },
    handleError(error) {
      const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
      if (httpStatus === 401) {
        this.$router.push('/login');
      } else {
        this.$store.dispatch('openNotification', {
          message: 'Update did not save. Please try again.',
          notificationType: 'error',
        });
      }
    },
    updateShareClassData() {
      // Update review status data for all share class funds to display in shared class section
      this.shareClassFundData.forEach((fund) => {
        // eslint-disable-next-line no-param-reassign
        fund.reviewStatus = this.newReviewStatus;
      });
    },
    loadFundDetails() {
      this.reviewLoading = true;
      FundReviewService.getFundDataPoints({ fundId: this.fundId }).then((response) => {
        this.reviewLoading = false;
        this.fundDetails = { ...response.data.list[0] };
        this.currentReviewStatus = this.fundDetails.reviewStatus;

        // Create input data for Change Status component
        const {
          fundID, fundStatusID, fundStatusName, isPending,
        } = this.fundDetails;
        this.fundStatusData = {
          fundID,
          approvalStatus: [{
            id: fundStatusID,
            name: fundStatusName,
          }],
          pending: isPending,
        };
        this.loadShareClassData();
      }).catch((error) => {
        this.reviewLoading = false;
        this.handleFundDetailsError(error);
      });
    },
    handleFundDetailsError(error) {
      const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
      if (httpStatus === 401) {
        this.$router.push('/login');
      } else {
        this.errorMsg = true;
      }
    },
    resetFields() {
      this.notesData = '';
      this.newReviewStatus = '';
    },
    setActiveItem(event) {
      /* eslint-disable no-param-reassign */
      this.tabContent.forEach((item) => {
        if (item.id === event.currentTarget.id) {
          item.active = true;
        } else {
          item.active = false;
        }
      });
      this.currentTab = event.currentTarget.id;
    },
    loadShareClassData() {
      this.shareClassLoading = true;
      FundReviewService.getShareClassFunds(this.fundId).then((response) => {
        this.shareClassLoading = false;
        if (response.status.success) {
          this.shareClassFundData = response.data.list;
        }
      }).catch((error) => {
        this.shareClassLoading = false;
        const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
        if (httpStatus === 401) {
          this.$router.push('/login');
        } else {
          this.$store.dispatch('openNotification', {
            message: 'Error in fetching share class funds.',
            notificationType: 'error',
          });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.fund-error {
  text-align: center;
  margin-top: $mds-space-5-x;
  @include mds-body-text-l($bold: true);
}
.update-status-container {
  padding-top: $mds-space-7-x;
    .fund-name-container {
    .field-value {
      @include mds-body-text-l($bold: true);
      margin-bottom: $mds-space-3-x;
      margin-top: 0;
    }
  }
  .fund-details-container {
    margin-bottom: $mds-space-3-x;
    display: flex;
    .fund-details {
      @include mds-body-text-m();
      &.fund-border {
        padding-right: $mds-space-1-and-a-half-x;
        margin-right: $mds-space-1-and-a-half-x;
        position:relative;
      }
      &.fund-border:after {
        content: "";
        position: absolute;
        top: 10%;
        right: 0;
        width: 1px;
        height: 80%;
        background: $mds-color-neutral-67;
      }
      .field-name {
        margin-right: $mds-space-three-quarter-x;
        @include mds-body-text-m($bold: true);
      }
    }
  }
}
.update-review-status-container {
  padding: $mds-space-5-x 0;
  .current-status-container {
    display: flex;
    margin-bottom: $mds-space-3-x;
    @include mds-body-text-l();
    .status-label {
      margin-right: $mds-space-1-x;
      @include mds-body-text-l($bold: true);
    }
    .status-value {
      display: flex;
      align-items: center;
      width: 200px;
    }
  }
  .change-status-box {
    width: 25%;
    margin-bottom: $mds-space-5-x;
  }
  .share-class-container {
    margin-bottom: $mds-space-5-x;
  }
  .add-notes-container {
    .notes-count {
      @include mds-body-text-m();
      float: right;
    }
  }
}

</style>
<style lang="scss">
.update-fund-status-container{
  .fund-series-warning {
    text-align: center;
    margin-top: $mds-space-5-x;
  }
  .change-status__header {
    display: none;
  }
  .change-status__content, .approve__content {
    padding: $mds-space-4-x 0;
  }
}
</style>
